import type { JSX } from 'react';
import * as React from 'react';
import { useCallback, useLayoutEffect, useRef } from 'react';

import classNames from 'classnames';

import styles from './Input.module.css';

type TextareaProps = React.ComponentProps<'textarea'> & {
  /**
   * Height follows the content, so a long value reads in full instead of
   * scrolling inside a box sized for a short one — which on a phone is most
   * boxes. `rows` becomes the minimum; there is no maximum, the page scrolls
   * instead. Re-measures on value changes and whenever the width changes
   * (rotation, sidebar, viewport), since the wrap point moves with it.
   */
  autoGrow?: boolean;
  /**
   * A title-style field: the text wraps onto as many lines as it needs, but it
   * is still one line of content. Enter inserts nothing and pasted line breaks
   * become spaces, so the stored value is what a single-line input would hold.
   * Use it with `autoGrow` in place of an `Input` whose value can run long.
   */
  singleLine?: boolean;
};

/**
 * What a `singleLine` field stores: line breaks, and the whitespace around
 * them, collapse to one space. Exported for values that reach a single-line
 * field's state without going through its onChange — a suggestion taken from
 * a list, say — so they hold the same shape as typed text.
 */
function toSingleLine(value: string): string {
  return value.replace(/\s*[\r\n]+\s*/g, ' ');
}

function Textarea({
  className,
  autoGrow = false,
  singleLine = false,
  rows,
  onChange,
  onKeyDown,
  ref,
  ...props
}: TextareaProps): JSX.Element {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (el: HTMLTextAreaElement | null) => {
      innerRef.current = el;
      if (typeof ref === 'function') ref(el);
      else if (ref) ref.current = el;
    },
    [ref],
  );

  const fit = useCallback(() => {
    const el = innerRef.current;
    if (!el || !autoGrow) return;
    // Reset first so it shrinks back when text is deleted. scrollHeight
    // excludes the border and the global box-sizing is border-box, so add it.
    el.style.height = 'auto';
    const computed = window.getComputedStyle(el);
    const border =
      (parseFloat(computed.borderTopWidth) || 0) + (parseFloat(computed.borderBottomWidth) || 0);
    el.style.height = `${el.scrollHeight + border}px`;
  }, [autoGrow]);

  useLayoutEffect(fit, [fit, props.value]);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el || !autoGrow || typeof ResizeObserver === 'undefined') return undefined;
    let width = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      // Only a width change moves the wrap point; reacting to our own height
      // writes would loop.
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [autoGrow, fit]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (singleLine && event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
    }
    onKeyDown?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (singleLine && /[\r\n]/.test(event.target.value)) {
      event.target.value = toSingleLine(event.target.value);
    }
    // A controlled field re-fits when its value prop changes; an uncontrolled
    // one has no prop to watch, so fit here as well.
    fit();
    onChange?.(event);
  };

  return (
    <textarea
      data-slot="textarea"
      ref={setRefs}
      rows={rows ?? (singleLine ? 1 : undefined)}
      enterKeyHint={singleLine ? 'done' : undefined}
      className={classNames(styles.Input, { [styles.AutoGrow]: autoGrow }, className)}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}

export { Textarea, toSingleLine };
